import React, { Component } from "react";
import NewsletterForm from "../Newsletter/NewsletterForm";
import Share from "./Share";
import { ScholarPumpkin } from "./Art";
import { Guide, Play, chooseGuide, closing, trackHalloween } from "./api";

// What they won: the code, how to use it, the booklet picker for a guide
// prize, the newsletter for anyone not ready to buy, and a way to pass the
// page on.

// Kept short (Chris, 27 Oct 2026): the title says what they won, and a line
// under it only where it's the punchline. The percentage is the one on their
// code, which is the year's setting in admin; "unlucky for some" only makes
// sense at 13, so a different trick prize just says what it is.
const copyFor = ({ prize, percent }: Play): { eyebrow: string; title: string; line?: string } => {
  switch (prize) {
    case "pct42":
      return { eyebrow: "The golden pumpkin", title: `${percent}% off. You found it!`, line: "One pumpkin in 42." };
    case "pct20":
      return { eyebrow: "Your treat", title: `${percent}% off. Frightfully good.` };
    case "pct13":
      return percent === 13
        ? { eyebrow: "Your treat", title: "13% off. Unlucky for some.", line: "Not for you, though." }
        : { eyebrow: "Your treat", title: `${percent}% off. Bewitching.` };
    default:
      return { eyebrow: "Your treat", title: `A tips booklet, and ${percent}% off.` };
  }
};

const COVERS = ["#233dff", "#ff645e", "#1f5538", "#7c4dce", "#0f766e", "#c2410c"];

interface Props {
  play: Play;
  endsAt: string | null;
  guides: Guide[];
  // True when this go was had earlier: the visitor is back to find their code.
  returning?: boolean;
  onPlayChanged: (play: Play) => void;
  onClose?: () => void;
}

interface State {
  copied: boolean;
  choosing: string | null;
  guideError: string;
  showNewsletter: boolean;
}

class Prize extends Component<Props, State> {
  state: State = { copied: false, choosing: null, guideError: "", showNewsletter: false };
  private copiedTimer?: number;

  componentWillUnmount() {
    window.clearTimeout(this.copiedTimer);
  }

  copyCode = () => {
    const { code } = this.props.play;
    trackHalloween("copy", { prize: this.props.play.prize });

    const done = () => {
      this.setState({ copied: true });
      window.clearTimeout(this.copiedTimer);
      this.copiedTimer = window.setTimeout(() => this.setState({ copied: false }), 2500);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(() => window.prompt("Copy your code", code));
    } else {
      window.prompt("Copy your code", code);
    }
  };

  pickGuide = (slug: string) => {
    this.setState({ choosing: slug, guideError: "" });
    chooseGuide(slug)
      .then((play) => {
        trackHalloween("guide", { course: slug });
        this.setState({ choosing: null });
        this.props.onPlayChanged(play);
      })
      .catch((error) => {
        this.setState({
          choosing: null,
          guideError: error?.response?.data?.error || "The cauldron hiccupped. Try that again?",
        });
      });
  };

  renderCover(index: number) {
    return (
      <span className="hw-book__cover" style={{ background: COVERS[index % COVERS.length] }} aria-hidden="true">
        <span className="hw-book__spine" />
        <span className="hw-book__label" />
      </span>
    );
  }

  renderGuides() {
    const { play, guides } = this.props;
    if (!play.guide_prize) return null;

    // Once chosen, the booklet keeps its row, with Download where Choose was.
    if (play.guide) {
      const index = Math.max(0, guides.findIndex((guide) => guide.slug === play.guide?.slug));
      return (
        <div className="hw-guides">
          <a className="hw-book hw-book--chosen" href={play.guide.url} target="_blank" rel="noopener noreferrer">
            {this.renderCover(index)}
            <span className="hw-book__text">
              <span className="hw-book__title">{play.guide.title}</span>
            </span>
            <span className="hw-book__action">Download</span>
          </a>
        </div>
      );
    }

    return (
      <div className="hw-guides">
        <p className="hw-guides__heading">Choose your tips booklet</p>
        <div className="hw-guides__grid">
          {guides.map((guide, index) => (
            <button
              key={guide.slug}
              type="button"
              className="hw-book"
              onClick={() => this.pickGuide(guide.slug)}
              disabled={this.state.choosing !== null}
            >
              {this.renderCover(index)}
              <span className="hw-book__text">
                <span className="hw-book__title">{guide.course_name}</span>
                <span className="hw-book__line">{guide.line}</span>
              </span>
              <span className="hw-book__action">{this.state.choosing === guide.slug ? "…" : "Choose"}</span>
            </button>
          ))}
        </div>
        {this.state.guideError && (
          <p className="hw-error" role="alert">
            {this.state.guideError}
          </p>
        )}
      </div>
    );
  }

  render() {
    const { play, returning, onClose } = this.props;
    const copy = copyFor(play);
    const ends = closing(this.props.endsAt);
    const eyebrow = play.path === "trick" ? "Every trick ends in a treat" : copy.eyebrow;

    return (
      <div className={`hw-prize hw-prize--${play.prize}`} aria-live="polite">
        <div className="hw-prize__top">
          <svg className="hw-prize__pumpkin" viewBox="0 0 120 120" aria-hidden="true">
            <ScholarPumpkin />
          </svg>
          <div>
            <p className="hw-eyebrow">{returning ? "Here's your treat again" : eyebrow}</p>
            <h2>{copy.title}</h2>
            {copy.line && <p className="hw-lead">{copy.line}</p>}
          </div>
        </div>

        <div className="hw-ticket">
          <span className="hw-ticket__label">Your code</span>
          <span className="hw-ticket__code">{play.code}</span>
          <button type="button" className="hw-btn hw-btn--small" onClick={this.copyCode}>
            {this.state.copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="hw-small">
          Use it once, on any course or the Pass{ends && `, by ${ends.time} ${ends.weekday} (UK time)`}.
        </p>

        {this.renderGuides()}

        <div className="hw-actions">
          <a className="hw-btn hw-btn--orange" href="/courses" onClick={() => trackHalloween("browse")}>
            Choose a course
          </a>
          {onClose && (
            <button type="button" className="hw-link" onClick={onClose}>
              I'll come back to it
            </button>
          )}
        </div>

        <div className="hw-newsletter">
          {this.state.showNewsletter ? (
            <NewsletterForm
              heading={
                <>
                  <h3>One useful idea every Tuesday</h3>
                  <p className="lead">And the week's best reads every Friday.</p>
                </>
              }
              buttonLabel="Sign me up"
              promise={null}
              onSubscribed={() => trackHalloween("newsletter")}
            />
          ) : (
            <button type="button" className="hw-link" onClick={() => this.setState({ showNewsletter: true })}>
              Not ready yet? Get our newsletter.
            </button>
          )}
        </div>

        <Share />
      </div>
    );
  }
}

export default Prize;
